import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfigurationService } from './configuration.service';
import { filter, firstValueFrom, from, lastValueFrom, Observable } from 'rxjs';
import { Persona } from '../models/personas.interface';
import { DriftConfiguration } from '../models/configuration.interface';
import { CollectionsLinks } from '../models/collection.interface';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import { DocumentInitParameters, TextItem } from 'pdfjs-dist/types/src/display/api';

@Injectable({
  providedIn: 'root'
})
export class PersonaService {
  config!: DriftConfiguration;
  FILE_TYPE_PDF = '.pdf';
  FILE_TYPE_TXT = '.txt';

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService
  ) {
    this.configurationService
      .configurationLoaded()
      .pipe(filter(loaded => loaded === true))
      .subscribe(() => {
        this.config = this.configurationService.getConfiguration();
      });
    GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.mjs';
  }

  getPersonas(includeHidden = false): Observable<Persona[]> {
    const url = `${this.config.driftApi}/personas`;
    let params = new HttpParams();
    if (includeHidden) {
      params = params.set('include_hidden', 'true');
    }
    return this.http.get<Persona[]>(url, { params });
  }

  getPersona(id: string): Observable<Persona> {
    return this.http.get<Persona>(`${this.config.driftApi}/personas/${id}`);
  }

  personaLog(persona: Persona): Observable<{ filename: string }> {
    const fileList = persona.exampleDocuments.map(file => file.s3_output_path);
    return this.http.post<{ filename: string }>(`${this.config.driftApi}/persona-log`, {
      flow_id: persona.id,
      files: fileList
    });
  }

  generatePersona(persona: Persona): Observable<{ description: string }> {
    // : Observable<{ description: string }>
    const fileList = persona.exampleDocuments.map(file => file.s3_output_path);
    return this.http.post<{ description: string }>(`${this.config.driftApi}/personas/${persona.id}/description`, {
      flow_id: persona.id,
      files: fileList,
      log_filename: persona.log_filename
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pollGenPersonaResult(filename: string): Observable<any> {
    const requestBody = { filename };
    return this.http.post(`${this.config.driftApi}/poll-persona-gen-result`, requestBody, {
      observe: 'response'
    });
  }

  updatePersona(persona: Persona) {
    return this.http.put<Persona>(`${this.config.driftApi}/personas/${persona.id}`, persona);
  }

  createPersona(persona: Persona) {
    return this.http.post<Persona>(`${this.config.driftApi}/personas`, persona);
  }

  deletePersona(persona: Persona) {
    return this.http.delete<Persona>(`${this.config.driftApi}/personas/${persona.id}`);
  }

  deleteUploadedDocument(personaId: string, fileName: string) {
    return this.http.delete(`${this.config.driftApi}/personas/${personaId}/links/${fileName}`);
  }

  convertPDFtoTxtAndUpload(id: string, files: File[], directory: string): Observable<object> {
    return from(this.uploadPdfFilesAsTxt(files, id, directory));
  }

  private async uploadPdfFilesAsTxt(fileArray: File[], id: string, directory: string) {
    const uploadLinkInformation = await this.getUploadLinksForPDFToTxt(fileArray, id, directory);
    const responses = [];

    for (const link of uploadLinkInformation.links) {
      try {
        const file = fileArray.find(f => f.name.replace(this.FILE_TYPE_PDF, this.FILE_TYPE_TXT) === link.file_name);
        if (file) {
          const text = await this.pdfToText(file);
          const txtFile = new File([text], file.name.replace(this.FILE_TYPE_PDF, this.FILE_TYPE_TXT), {
            type: this.FILE_TYPE_TXT
          });
          responses.push(
            await lastValueFrom(
              this.http.put(link.url, txtFile, {
                reportProgress: true,
                observe: 'events',
                headers: { 'Content-Type': 'text/plain; charset=utf-8' }
              })
            )
          );
        } else {
          console.error(`Upload details missing for file: ${link.file_name}`);
        }
      } catch (error) {
        console.error(`Error uploading file ${link.file_name}.`, error);
        throw error;
      }
    }
    return responses;
  }

  private async getUploadLinksForPDFToTxt(fileArray: File[], id: string, directory: string): Promise<CollectionsLinks> {
    try {
      const data = { files: fileArray.map(file => file.name.replace(this.FILE_TYPE_PDF, this.FILE_TYPE_TXT)) };
      return await firstValueFrom(
        this.http.post<CollectionsLinks>(`${this.config.driftApi}/${directory}/${id}/links`, data)
      );
    } catch (error) {
      console.error('Error generating file links', error);
      throw error;
    }
  }

  private async pdfToText(file: File) {
    const content = new Uint8Array(await file.arrayBuffer());
    let text = '';
    try {
      const docParams: DocumentInitParameters = { data: content };
      const pdf = await getDocument(docParams).promise;
      for (let i = 1; i <= pdf.numPages; i++) {
        console.debug(`converting page ${i} / ${pdf.numPages}`);
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent({});
        const pageText = textContent.items
          .map(item => {
            const b = item as TextItem;
            return b.str;
          })
          .join(' ');
        text += pageText + '\n';
      }
    } catch (error) {
      console.error('Error converting PDF to txt', error);
      throw error;
    }
    return text;
  }
}
